import _defineProperty from "@babel/runtime/helpers/defineProperty";
function ownKeys(e, r) { var t = Object.keys(e); if (Object.getOwnPropertySymbols) { var o = Object.getOwnPropertySymbols(e); r && (o = o.filter(function (r) { return Object.getOwnPropertyDescriptor(e, r).enumerable; })), t.push.apply(t, o); } return t; }
function _objectSpread(e) { for (var r = 1; r < arguments.length; r++) { var t = null != arguments[r] ? arguments[r] : {}; r % 2 ? ownKeys(Object(t), !0).forEach(function (r) { _defineProperty(e, r, t[r]); }) : Object.getOwnPropertyDescriptors ? Object.defineProperties(e, Object.getOwnPropertyDescriptors(t)) : ownKeys(Object(t)).forEach(function (r) { Object.defineProperty(e, r, Object.getOwnPropertyDescriptor(t, r)); }); } return e; }
import { types, getParent, getRoot, cast } from 'mobx-state-tree';
import { ALLOCATION_METHOD } from 'src/constants/utilities';
import { SubmeterBillFinalStateModel } from './property/allocations';
const Period = types.model({
  allocationCharge: types.number,
  allocationId: types.string,
  occupancyFactor: types.number,
  periodEndDateOnly: types.string,
  periodStartDateOnly: types.string,
  residentShare: types.number
});
const Allocation = types.model({
  allocationCharge: types.number,
  allocationId: types.string,
  billingCycleTotal: types.maybeNull(types.number),
  correction: types.maybeNull(types.number),
  commonAreaDeduction: types.maybeNull(types.number),
  daysOccupied: types.number,
  isBillable: types.boolean,
  occupancyFactor: types.number,
  occupantsCount: types.number,
  periods: types.array(Period),
  proRataFactor: types.number,
  proratedOccupancyFactor: types.number,
  residentAccountId: types.maybeNull(types.string),
  residentId: types.maybeNull(types.string),
  unitName: types.string,
  unitOccupancyFactor: types.number
});
const Bill = types.model({
  amount: types.maybe(types.number),
  // LB-5835 / LB-5844: submeter utilities only, optional decimals. MST drops any key not
  // declared here, so these three must be named explicitly for a real fetch response to seed
  // the form's Consumption charges / Base charge / Bill usage inputs. types.maybe(types.maybeNull(...)),
  // not a bare types.maybe: the columns are nullable on the backend and there is no
  // ignore-null-on-serialize setting on that service, so an existing bill with these columns
  // still NULL very likely comes back as an explicit `null`, not an absent key, once #658
  // ships. A bare types.maybe accepts absent but rejects null and would throw on that
  // response, breaking the allocation table load for every utility, RUBS included.
  baseChargeAmount: types.maybe(types.maybeNull(types.number)),
  consumptionCharges: types.maybe(types.maybeNull(types.number)),
  endDateOnly: types.maybe(types.string),
  fileId: types.maybeNull(types.string),
  redactedFileId: types.maybeNull(types.string),
  // The bill-automation bill this allocation was made from (LB-5898), which is how apply
  // billing shows the document it allocated rather than whichever bill the cycle answers with.
  //
  // maybeNull, like the two fields above: it is an optional whose default is null, so it takes
  // the key present, absent, or explicitly null alike. Absent is the ordinary case, not a
  // hypothetical — the generated DTO this reaches us through carries NullValueHandling.Ignore, so
  // a bill nobody allocated simply has no such key. A required type here would not throw either:
  // setUtilityAfterFetch swallows a rejected snapshot, so the utility would quietly fail to bind
  // and the whole allocation table would come up empty.
  sourceBillId: types.maybeNull(types.string),
  // The account number the bill is for, kept with it since BIB-458. maybeNull for the same reasons as sourceBillId:
  // absent from a server that predates it, and null on a bill that has none.
  accountNumber: types.maybeNull(types.string),
  goodwillDeduction: types.maybe(types.number),
  id: types.maybe(types.string),
  isSkipped: types.maybe(types.boolean),
  isShared: types.maybe(types.maybeNull(types.boolean)),
  startDateOnly: types.maybe(types.string),
  totalUsage: types.maybe(types.maybeNull(types.number)),
  utilityId: types.maybe(types.string)
}).actions(store => {
  return {
    changeRedactedFileId(data) {
      store.redactedFileId = data;
    },
    changeIsSharedFile(data) {
      store.isShared = data;
    },
    changeFileId(data) {
      store.fileId = data;
    },
    changeSourceBillId(data) {
      store.sourceBillId = data;
    }
  };
});
const AllocationsComparison = types.model({
  activeUnits: types.number,
  allocatedTotal: types.number,
  billId: types.maybeNull(types.string),
  billValue: types.maybeNull(types.number),
  commonAreaDeduction: types.number,
  goodwillDeduction: types.maybeNull(types.number),
  month: types.number,
  serviceEndDateOnly: types.maybeNull(types.string),
  serviceStartDateOnly: types.maybeNull(types.string),
  totalBilledToResident: types.number,
  totalOccupants: types.number,
  year: types.number
});
const Comparison = types.model({
  allocations: types.array(AllocationsComparison),
  companyId: types.string,
  currentBillValue: types.maybeNull(types.number),
  isBillIncreasedBy20Percent: types.maybeNull(types.boolean),
  isReviewed: types.maybeNull(types.boolean),
  lastBillValue: types.maybeNull(types.number),
  propertyId: types.string,
  utilityId: types.string
});
const AllocationUtility = types.model({
  allocated: types.number,
  allocationMethod: types.string,
  allocations: types.optional(types.array(Allocation), []),
  bill: types.maybeNull(Bill),
  billStatus: types.maybeNull(types.string),
  billTotal: types.number,
  bills: types.optional(types.array(Bill), []),
  commonAreaDeduction: types.number,
  comparison: types.maybeNull(Comparison),
  deductions: types.number,
  defaultGoodwillDeduction: types.number,
  isApproved: types.boolean,
  isEditable: types.boolean,
  isReviewCheckedValue: types.optional(types.boolean, false),
  isSkipped: types.boolean,
  ratioBilledTotal: types.number,
  recovery: types.number,
  utilityId: types.string,
  utilityType: types.maybeNull(types.string)
}).actions(store => {
  return {
    setReviewChecked(value) {
      try {
        store.isReviewCheckedValue = value;
      } catch (error) {
        console.error('STORE ERROR > ', error);
      }
    },
    setComparisonAfterFetch(data) {
      try {
        store.comparison = data.response;
      } catch (error) {
        console.error('STORE ERROR > ', error);
      }
    }
  };
}).views(store => {
  return {
    get isReviewChecked() {
      return store.isReviewCheckedValue;
    }
  };
});
const Preview = types.model('Preview', {
  allocated: types.maybeNull(types.number),
  additionalSkipComment: types.maybeNull(types.string),
  allocationMethod: types.string,
  allocationTableSkipReason: types.maybeNull(types.string),
  billAmount: types.maybeNull(types.number),
  billEndDateOnly: types.maybeNull(types.string),
  billStartDateOnly: types.maybeNull(types.string),
  billingCycleId: types.maybeNull(types.string),
  billStatus: types.string,
  deductions: types.maybeNull(types.number),
  fileId: types.maybeNull(types.string),
  isEditable: types.boolean,
  isSkipped: types.boolean,
  otherSkipReason: types.maybeNull(types.string),
  redactedFileId: types.maybeNull(types.string),
  utilityId: types.string,
  submeterFinalState: types.maybeNull(types.array(SubmeterBillFinalStateModel))
}).actions(store => {
  return {
    changeRedactedFileId(data) {
      store.redactedFileId = data;
    },
    changeFileId(data) {
      store.fileId = data;
    },
    updateSubmeterFinalState(data) {
      if (Array.isArray(data) && !!(data !== null && data !== void 0 && data.length)) {
        store.submeterFinalState = data;
      } else {
        store.submeterFinalState = null;
      }
    },
    fetchSubmeterFinalState(_ref) {
      let billingCycleId = _ref.billingCycleId;
      const parentPropertyObj = getParent(getParent(getParent(getParent(getParent(store)))));
      if (store.allocationMethod === ALLOCATION_METHOD.BySubmeter) {
        var _getRoot, _store$billingCycleId, _store$billingCycleId2;
        return (_getRoot = getRoot(store)) === null || _getRoot === void 0 ? void 0 : _getRoot.allocations.postGetSubMeterFinalStateAsync.run({
          // allocationMethod: store.allocationMethod,
          utilityId: store.utilityId,
          propertyId: parentPropertyObj.id,
          billingCycleId: (_store$billingCycleId = store.billingCycleId) !== null && _store$billingCycleId !== void 0 ? _store$billingCycleId : billingCycleId
        }, [store.utilityId, (_store$billingCycleId2 = store.billingCycleId) !== null && _store$billingCycleId2 !== void 0 ? _store$billingCycleId2 : billingCycleId, parentPropertyObj.id]).then(response => {
          if (response !== null && response !== void 0 && response.isSuccessful) {
            store.updateSubmeterFinalState(response.response);
          }
        });
      } else {
        return new Promise(resolve => {
          setTimeout(() => {
            resolve(null);
          }, 1);
        });
      }
    }
  };
});
const BillingCycleModel = types.model('BillingCycleModel', {
  isDefaultBillingCycle: types.boolean,
  isHistoricalAllocationCycle: types.boolean,
  utilityTables: types.array(Preview)
});
export const CycleChargesPreview = types.model({
  residentAccountId: types.maybeNull(types.string),
  residentId: types.maybeNull(types.string),
  totalAmount: types.number,
  unitId: types.maybeNull(types.string),
  unitName: types.maybeNull(types.string),
  utilityItems: types.array(types.model({
    amount: types.number,
    utilityId: types.maybeNull(types.string)
  }))
});
export const BillingSummary = types.model('BillingSummary', {
  allocatedTotal: types.number,
  allocationCycleId: types.string,
  billingCycleSummaries: types.array(types.model({
    allocatedTotal: types.number,
    billingCycleId: types.string,
    billsTotal: types.number,
    isDefaultBillingCycle: types.boolean
  })),
  billsTotal: types.number,
  cycleChargesPreview: types.optional(types.array(CycleChargesPreview), []),
  cycleName: types.string,
  cycleStatus: types.maybeNull(types.string),
  cycleUtilityTableStatuses: types.string,
  isCurrentAllocationCycle: types.boolean,
  month: types.number,
  propertyId: types.string,
  statuses: types.array(types.model({
    billStatus: types.string,
    count: types.number
  })),
  year: types.number
});
export const DateRange = types.model('DateRange', {
  endDateOnly: types.maybeNull(types.number),
  startDateOnly: types.maybeNull(types.number)
});
const TempFileData = types.model('TempFileData', {
  fileId: types.maybeNull(types.string),
  isShared: types.maybeNull(types.boolean),
  isReducted: types.maybeNull(types.boolean),
  isDeleted: types.maybeNull(types.boolean),
  billAutomationResponse: types.maybeNull(types.frozen()),
  /**
   * True when the bill on screen was recovered for this cycle rather than uploaded just now — the one
   * thing that separates "you left this unfinished" from "you have only this moment uploaded it", and
   * the two need saying differently.
   */
  wasRecovered: types.maybeNull(types.boolean),
  /**
   * True from the moment a replacement bill starts on its way until one has actually arrived.
   *
   * The screen lets go of the bill being replaced before the new one exists, so until then it holds
   * nothing while the allocation still names the old bill -- and anything that went looking would
   * fetch that bill straight back, beside the file replacing it (LB-5898).
   *
   * It outlives the attempt rather than the request, which is the point: a replacement that FAILED
   * leaves the newly chosen file on screen with no extraction of its own, and that is exactly the
   * gap recovery must not fill. It stays set until a replacement succeeds or the screen is reset,
   * which happens on every open, close and step to another utility.
   */
  hasUnfinishedReplacement: types.maybeNull(types.boolean),
  /**
   * The allocation made from a bill that was removed here (LB-6028). Its bill is not fetched back, neither through that
   * allocation, still in the store until its reload lands, nor as the cycle's bill; an allocation made from the same bill
   * again since is a different one, and its bill is fetched as ever. Cleared with the rest when the modal closes or steps
   * to another utility.
   */
  releasedAllocationBillId: types.maybeNull(types.string),
  /**
   * Bills for this table's property and utility scheduled for a later month (LB-6009), asked for when the table has no
   * bill of its own: "A later bill is on file". Cleared with the rest when the screen starts over.
   */
  scheduledLaterBills: types.maybeNull(types.frozen()),
  /**
   * The bill whose read has filled Bill details on this screen (BIB-458). Edit mounts the form afresh over the bill saved
   * since, and the read is not filled over that again: what was saved is what the PM made of it. Cleared with the rest
   * when the screen starts over, so another upload, or the screen opened again, fills as ever.
   */
  appliedReadBillId: types.maybeNull(types.string),
  /**
   * The bill whose read the PM set aside to enter the bill themselves ("Enter it myself", BIB-458). Its read fills
   * nothing on this screen, whether it is still being read or already was; the bill stays on screen and is what a save
   * links. Cleared with the rest when the screen starts over, and by "Use the read instead".
   */
  readSetAsideForBillId: types.maybeNull(types.string),
  /**
   * The original of a duplicate, taken here as this table's bill with "Use the original bill for this table" (LB-6016).
   * Held as chosen here, so nothing is fetched over it, but never as uploaded here: the bill came from elsewhere -- the
   * Bills page, an email -- and is not this screen's to delete.
   */
  takenOriginalBillId: types.maybeNull(types.string),
  /**
   * How many times the screen has started over (resetTempFileData). A request still out when it does can tell that what
   * the screen holds now is newer than the request (LB-6028).
   */
  resetCount: types.optional(types.number, 0)
});
const BillAllocation = types.model({
  billId: types.maybeNull(types.string),
  billStatus: types.maybeNull(types.string),
  billingCycleId: types.maybeNull(types.string),
  billValue: types.maybeNull(types.number),
  commonAreaDeduction: types.maybeNull(types.number),
  goodwillDeduction: types.maybeNull(types.number),
  rationBilled: types.maybeNull(types.number),
  serviceEndDateOnly: types.maybeNull(types.string),
  serviceStartDateOnly: types.maybeNull(types.string),
  submetered: types.maybeNull(types.number),
  total: types.maybeNull(types.number),
  utilityId: types.maybeNull(types.string)
});
const AllocationHistory = types.model('AllocationHistory', {
  allocationCycleId: types.maybeNull(types.string),
  month: types.maybeNull(types.number),
  propertyId: types.maybeNull(types.string),
  utilities: types.optional(types.array(BillAllocation), []),
  year: types.maybeNull(types.number),
  ym: types.maybeNull(types.number)
});
const AllocationsModel = types.model('AllocationsModel', {
  billingCycleIds: types.map(types.array(types.string)),
  billingCycles: types.map(BillingCycleModel),
  billingSummaryValue: types.maybeNull(types.array(BillingSummary)),
  comparisonById: types.map(Comparison),
  cycleChargesPreviewByAllocationId: types.map(types.array(CycleChargesPreview)),
  dateRangeValue: types.optional(DateRange, {
    endDateOnly: null,
    startDateOnly: null
  }),
  filterStatusesValue: types.optional(types.array(types.string), ['all']),
  statusValue: types.optional(types.string, 'all'),
  utilityById: types.map(AllocationUtility),
  allocationHistory: types.optional(types.array(AllocationHistory), []),
  tempFileData: types.optional(TempFileData, {
    fileId: null,
    isShared: null,
    isDeleted: null,
    isReducted: null,
    billAutomationResponse: null,
    wasRecovered: null,
    hasUnfinishedReplacement: null,
    releasedAllocationBillId: null,
    appliedReadBillId: null,
    readSetAsideForBillId: null,
    takenOriginalBillId: null
  })
}).actions(store => {
  return {
    deleteCycle(id) {
      try {
        store.billingCycles.delete(id);
      } catch (error) {
        console.error('STORE ERROR > ', error);
      }
    },
    removeIdFromBillingCycleIds(allocationCycleId, id) {
      try {
        const ids = store.billingCycleIds.get(allocationCycleId);
        if (ids) {
          const index = ids.indexOf(id);
          if (index > -1) {
            ids.splice(index, 1);
            store.billingCycleIds.set(allocationCycleId, ids);
          }
        }
      } catch (error) {
        console.error('STORE ERROR > ', error);
      }
    },
    /**
     * @param keepReleased whether to remember a bill let go of by a removal here (LB-6028): for a reset that only lets
     *   go of another bill, such as one deleted, while the modal stays on the same table.
     */
    resetTempFileData() {
      let _ref2 = arguments.length > 0 && arguments[0] !== undefined ? arguments[0] : {},
        _ref2$keepReleased = _ref2.keepReleased,
        keepReleased = _ref2$keepReleased === void 0 ? false : _ref2$keepReleased;
      store.tempFileData.fileId = null;
      store.tempFileData.isShared = null;
      store.tempFileData.isDeleted = null;
      store.tempFileData.isReducted = null;
      store.tempFileData.billAutomationResponse = null;
      store.tempFileData.wasRecovered = null;
      // Cleared with the rest. It deliberately survives a failed replacement, so this is the only
      // thing that lets go of it -- without which a screen reopened after one would never recover
      // a document at all.
      store.tempFileData.hasUnfinishedReplacement = null;
      store.tempFileData.appliedReadBillId = null;
      store.tempFileData.readSetAsideForBillId = null;
      store.tempFileData.takenOriginalBillId = null;
      store.tempFileData.scheduledLaterBills = null;
      store.tempFileData.resetCount += 1;
      if (keepReleased) return;
      store.tempFileData.releasedAllocationBillId = null;
    },
    setBillingCycleIds(allocationCycleId, data) {
      try {
        store.billingCycleIds.set(allocationCycleId, data);
      } catch (error) {
        console.error('STORE ERROR > ', error);
      }
    },
    setTempFileData(key, value) {
      store.tempFileData[key] = value;
    },
    setBillingSummaryAfteFetch(data) {
      try {
        store.billingSummaryValue = data;
      } catch (error) {
        console.error('STORE ERROR > ', error);
      }
    },
    setChargesPreviewAfterFetch(id, data) {
      try {
        store.cycleChargesPreviewByAllocationId.set(id, data);
      } catch (error) {
        console.error('STORE ERROR > ', error);
      }
    },
    setAllocationHistory(data) {
      try {
        store.allocationHistory = cast([]);
        store.allocationHistory = cast(data);
      } catch (error) {
        console.error('STORE ERROR > ', error);
      }
    },
    setComparisonByIdAfterFetch(id, data) {
      try {
        store.comparisonById.set(id, data.response);
      } catch (error) {
        console.error('STORE ERROR > ', error);
      }
    },
    setCycleAfterFetch(id, data) {
      try {
        store.billingCycles.set(id, _objectSpread(_objectSpread({}, data.response), {}, {
          billingCycleId: id
        }));
      } catch (error) {
        console.error('STORE ERROR > ', error);
      }
    },
    setDateRange(data) {
      try {
        store.dateRangeValue = data;
      } catch (error) {
        console.error('STORE ERROR > ', error);
      }
    },
    setFilterStatuses(value) {
      try {
        store.filterStatusesValue = value;
      } catch (error) {
        console.error('STORE ERROR > ', error);
      }
    },
    setStatus(value) {
      try {
        store.statusValue = value;
      } catch (error) {
        console.error('STORE ERROR > ', error);
      }
    },
    setUtilityAfterFetch(billingCycleId, utilityId, data) {
      try {
        if (data.response.utilityTables.length > 0) store.utilityById.set(billingCycleId + utilityId, data.response.utilityTables.find(utility => utility.utilityId === utilityId));
      } catch (error) {
        console.error('STORE ERROR > ', error);
      }
    }
  };
}).views(store => {
  return {
    get billingSummary() {
      return store.billingSummaryValue;
    },
    get dateRange() {
      return store.dateRangeValue;
    },
    get filterStatuses() {
      return store.filterStatusesValue;
    },
    get status() {
      return store.statusValue;
    }
  };
});
export default AllocationsModel;